import { Link } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { allObjectives, domainColor, tiersOf } from '@/lib/indexes/registry'
import { tierScore } from '@/lib/readiness/score'
import { assertScale } from '@/lib/readiness/scale'
import { RdPage } from '@/components/readiness/shell'
import { PreliminaryNotice } from '@/components/readiness/PreliminaryNotice'
import { Chip, DomainDot, Icon, SectionLabel } from '@/components/readiness/primitives'

// "How this is scored" (I2 §11.4): lists each sub-index's tiers and their
// source pages for a preliminary index, and the level model for a levelled one.
export default function Scoring() {
  const { index, indexId } = useReadiness()
  const { theme } = useTheme()
  const scale = assertScale(index)
  const q = `?index=${indexId}`
  return (
    <RdPage title="How this is scored" subtitle={`${index.name} ${index.version} · ${index.publisher}`} testId="readiness-scoring">
      <PreliminaryNotice index={index} />
      <Link to={`/readiness${q}`} className="rd-link mb-4 inline-flex items-center gap-1 text-[14px]"><Icon name="arrow-left" size={14} /> Dashboard</Link>

      <section className="rd-card mb-4 p-5">
        <SectionLabel>Method</SectionLabel>
        {scale === 'score_0_10' ? (
          <div className="mt-2 space-y-2 text-[14px]" style={{ color: 'var(--rd-text-secondary)' }}>
            <p>Each sub-index carries three or four tier statements derived from the answer bands visible in the report's survey findings. Tier <span className="rd-mono">n</span> of <span className="rd-mono">m</span> maps to a score of <span className="rd-mono">10 × (n−1)/(m−1)</span>, rounded to one decimal. A user may enter a different score with a note, for example their own regulator survey result.</p>
            <p>Dimension score = mean of its scored sub-indices; overall = mean of the {index.domains.length} dimensions; unscored sub-indices are excluded and the score card says so. Suggestions from Discovery, Playbook and saved assessments are capped at tier 2 of <span className="rd-mono">m</span>.</p>
            {index.benchmarks && <p>Sector benchmarks: {Object.entries(index.benchmarks.by_label ?? {}).map(([k, v]) => `${k} ${v.toFixed(1)}`).join(' · ')} ({index.benchmarks.source}).</p>}
            <p style={{ color: 'var(--rd-text-faint)' }}>{index.transcription.note}</p>
          </div>
        ) : (
          <div className="mt-2 space-y-2 text-[14px]" style={{ color: 'var(--rd-text-secondary)' }}>
            <p>Each objective is assessed at one of four levels with the published descriptors. The readiness score is Quantizant's summary indicator: the mean level across all objectives as a percentage of L3. It is not part of the index.</p>
            <ul className="list-disc pl-5">
              {index.level_scale.map((l) => <li key={l.code}><span className="rd-mono">{l.code}</span> {l.name} · {l.description}{l.ref ? <span style={{ color: 'var(--rd-text-faint)' }}> ({l.ref})</span> : null}</li>)}
            </ul>
          </div>
        )}
      </section>

      {[...index.domains].sort((a, b) => a.order - b.order).map((d) => (
        <section key={d.code} className="rd-card mb-4 p-5" aria-labelledby={`sc-${d.code}`}>
          <h2 id={`sc-${d.code}`} className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: domainColor(d, theme) }}>
            <DomainDot domain={d} theme={theme} /> {d.name}
            {scale === 'score_0_10' && d.benchmark && <Chip tone="neutral">sector mean {d.benchmark.sector.toFixed(1)}</Chip>}
          </h2>
          <div className="mt-3 space-y-4">
            {allObjectives(index).filter((x) => x.domain.code === d.code).map(({ objective }) => (
              <div key={objective.code}>
                <p className="text-[14px] font-semibold"><span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{objective.code}</span> {objective.title}{objective.benchmark ? <span className="rd-mono text-[11.5px] font-normal" style={{ color: 'var(--rd-text-faint)' }}> · sector {objective.benchmark.sector.toFixed(1)}</span> : null}</p>
                {scale === 'score_0_10' ? (
                  <table className="mt-1.5 w-full text-[12.5px]">
                    <tbody>
                      {tiersOf(index, objective.code).map((t) => (
                        <tr key={t.n} style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
                          <td className="rd-mono py-1.5 pr-3 align-top" style={{ width: 110, color: 'var(--rd-text-muted)' }}>Tier {t.n} · {tierScore(t.n, tiersOf(index, objective.code).length).toFixed(1)}</td>
                          <td className="py-1.5 pr-3 align-top">{t.statement}</td>
                          <td className="py-1.5 align-top text-right" style={{ width: 220, color: 'var(--rd-text-faint)' }}>{t.source_page}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <ul className="mt-1.5 space-y-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>
                    {(['L0', 'L1', 'L2', 'L3'] as const).map((l) => <li key={l}><span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{l}</span> {objective.levels[l]}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </RdPage>
  )
}
